import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import HelpTooltip from '@/components/help/HelpTooltip';
import helpContent from '@/help/helpContent';
import {
  Wrench, Plus, Eye, CheckCircle, Archive, Copy, ChevronRight, AlertTriangle,
  Shield, X, Settings2, Play, Server, CalendarCheck2, Code2, Clock, Search,
  UserPlus, PhoneCall, History, Layers
} from 'lucide-react';

// Sprint 44 (1/N) — catalog loaded from GET /tenant/ai-training/builtin-tools
// (ai_tool_registry.py). Per-channel toggles still live in channel-rules.
const BUILTIN_STATUS_STYLES = {
  shipped: 'bg-emerald-50 text-emerald-700',
  planned: 'bg-slate-100 text-slate-600',
};
const BUILTIN_STATUS_LABELS = {
  shipped: 'Disponible',
  planned: 'Próximamente',
};

const CATEGORIES = ['knowledge','ai','crm','agenda','messaging','email','voice','reports','external_api','mcp','custom'];
const TOOL_TYPES = ['internal_read','internal_prepare','internal_mutation_future','external_api_future','webhook_future','mcp_future','custom_future'];
const RISK_LEVELS = ['low','medium','high','critical'];
const APPROVAL_MODES = ['none','human_required','admin_required','future'];
const EXEC_MODES = ['disabled','dry_run','future_action'];
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', published: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500', deprecated: 'bg-amber-50 text-amber-700',
};
const AVAIL_COLORS = {
  available: 'bg-emerald-50 text-emerald-700', stub: 'bg-blue-50 text-blue-700',
  future: 'bg-violet-50 text-violet-700', disabled: 'bg-gray-100 text-gray-500',
};

// Historial de pruebas (GET /tenant/ai-tools/logs) y versiones publicadas
// (GET /tenant/ai-tools/{id}/versions).
const LOGS_PAGE_SIZE = 20;
const LOG_STATUS_STYLES = {
  success: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
};
const LOG_STATUS_LABELS = {
  success: 'Prueba correcta',
  failed: 'Prueba con errores',
};
const LOG_TYPE_LABELS = {
  dry_run: 'Simulación',
};
const VERSION_STATUS_STYLES = {
  published: 'bg-emerald-50 text-emerald-700',
  superseded: 'bg-gray-100 text-gray-600',
};
const VERSION_STATUS_LABELS = {
  published: 'En uso',
  superseded: 'Reemplazada',
};

const formatDateTime = (iso) => {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Sin fecha';
  return d.toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function AIToolRegistry() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('list');
  const [tools, setTools] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ key:'', name:'', category:'custom', tool_type:'custom_future', execution_mode:'disabled', risk_level:'low', approval_mode:'none', input_schema:'{}', output_schema:'{}' });
  const [validateResult, setValidateResult] = useState(null);

  // Historial de pruebas (dry run) — se carga sólo al abrir la pestaña.
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [logsPage, setLogsPage] = useState(1);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsLoaded, setLogsLoaded] = useState(false);

  // Versiones publicadas de una herramienta — se cargan al abrir el modal.
  const [versionsTool, setVersionsTool] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');

  // Sprint 38 — curated built-in tools state (channel rules carry the
  // per-channel tools_enabled toggle; see training_resolver.py).
  const [advancedMode, setAdvancedMode] = useState(false);
  const [channelRules, setChannelRules] = useState([]);
  const [builtinCatalog, setBuiltinCatalog] = useState([]);
  const [builtinLoading, setBuiltinLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState('');

  const loadChannelRules = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/ai-training/channel-rules');
      setChannelRules(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  const loadBuiltinCatalog = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/ai-training/builtin-tools');
      setBuiltinCatalog(Array.isArray(data) ? data : (data?.tools || []));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBuiltinCatalog([]);
    } finally {
      setBuiltinLoading(false);
    }
  }, []);

  const isToolEnabled = (channel, toolKey) => {
    const rule = channelRules.find(r => r.channel === channel);
    return !!(rule?.config?.tools_enabled || {})[toolKey];
  };

  const toggleTool = async (channel, toolKey, next) => {
    setSavingToggle(`${channel}:${toolKey}`);
    try {
      const rule = channelRules.find(r => r.channel === channel);
      const nextConfig = { ...(rule?.config || {}), tools_enabled: { ...(rule?.config?.tools_enabled || {}), [toolKey]: next } };
      await api.put(`/tenant/ai-training/channel-rules/${channel}`, { config: nextConfig });
      toast.success(next ? 'Herramienta activada' : 'Herramienta desactivada');
      await loadChannelRules();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingToggle(''); }
  };

  const load = useCallback(async () => {
    try {
      const [tRes, tmplRes] = await Promise.all([
        api.get('/tenant/ai-tools'),
        api.get('/tenant/ai-tools/templates').catch(() => ({ data: [] })),
      ]);
      setTools(tRes.data || []);
      setTemplates(tmplRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loadChannelRules(); loadBuiltinCatalog(); }, [load, loadChannelRules, loadBuiltinCatalog]);

  const loadLogs = useCallback(async (page = 1, append = false) => {
    setLogsLoading(true);
    setLogsError('');
    try {
      const { data } = await api.get('/tenant/ai-tools/logs', { params: { page, page_size: LOGS_PAGE_SIZE } });
      const rows = Array.isArray(data) ? data : [];
      setLogs(prev => (append ? [...prev, ...rows] : rows));
      setLogsPage(page);
      setLogsHasMore(rows.length === LOGS_PAGE_SIZE);
      setLogsLoaded(true);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No pudimos cargar el historial de pruebas.';
      setLogsError(msg);
      toast.error(msg);
      setLogsLoaded(true);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // Carga perezosa: sólo cuando el usuario entra a la pestaña «Historial».
  useEffect(() => {
    if (tab === 'logs' && !logsLoaded) loadLogs(1, false);
  }, [tab, logsLoaded, loadLogs]);

  const toolNameById = (id) => {
    if (!id) return 'Herramienta eliminada';
    const found = tools.find(t => t.id === id);
    return found ? found.name : 'Herramienta eliminada';
  };

  const openVersions = async (tool) => {
    setVersionsTool(tool);
    setVersions([]);
    setVersionsError('');
    setVersionsLoading(true);
    try {
      const { data } = await api.get(`/tenant/ai-tools/${tool.id}/versions`);
      setVersions(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No pudimos cargar las versiones de esta herramienta.';
      setVersionsError(msg);
      toast.error(msg);
    } finally {
      setVersionsLoading(false);
    }
  };

  const closeVersions = () => { setVersionsTool(null); setVersions([]); setVersionsError(''); };

  const shippedBuiltin = builtinCatalog.filter((t) => t.status === 'shipped');
  const plannedBuiltin = builtinCatalog.filter((t) => t.status === 'planned');

  const renderBuiltinToolCard = (tool, { interactive }) => {
    const Icon = tool.key === 'agendar_cita' ? CalendarCheck2
      : tool.key === 'search_catalog' ? Search
      : tool.key === 'create_lead' ? UserPlus
      : tool.key === 'schedule_ai_call' ? PhoneCall
      : Wrench;
    const iconBg = interactive ? 'bg-emerald-50' : 'bg-slate-50';
    const iconColor = interactive ? 'text-emerald-600' : 'text-slate-400';
    const anyChannelOn = (tool.channels || []).some((ch) => isToolEnabled(ch.key, tool.key));

    return (
      <div
        key={tool.key}
        className={`bg-white border rounded-xl p-4 ${interactive ? 'border-[#E2E8F0]' : 'border-dashed border-[#CBD5E1] opacity-90'}`}
        data-testid={`builtin-tool-${tool.key}`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-[#0F172A]">{tool.name}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${BUILTIN_STATUS_STYLES[tool.status] || BUILTIN_STATUS_STYLES.planned}`}>
                {BUILTIN_STATUS_LABELS[tool.status] || tool.status}
              </span>
              <HelpTooltip text={tool.description} />
            </div>
            <p className="text-xs text-[#64748B] mt-0.5">{tool.description}</p>
            {interactive && anyChannelOn && tool.help_when_on && (
              <p className="text-xs text-emerald-700 mt-2 bg-emerald-50 rounded-lg px-2 py-1.5">{tool.help_when_on}</p>
            )}
            {interactive && !anyChannelOn && tool.help_when_off && (
              <p className="text-xs text-[#64748B] mt-2 bg-[#F8F9FA] rounded-lg px-2 py-1.5">{tool.help_when_off}</p>
            )}
            {!interactive && tool.help_when_off && (
              <p className="text-xs text-[#94A3B8] mt-2 bg-[#F8F9FA] rounded-lg px-2 py-1.5">{tool.help_when_off}</p>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 pl-12">
          {(tool.channels || []).map((ch) => (
            <div key={ch.key} className="flex items-center justify-between bg-[#F8F9FA] rounded-lg px-3 py-2">
              <span className="text-xs text-[#334155]">{ch.label}</span>
              <Switch
                checked={interactive ? isToolEnabled(ch.key, tool.key) : false}
                disabled={!interactive || savingToggle === `${ch.key}:${tool.key}`}
                onCheckedChange={interactive ? (next) => toggleTool(ch.key, tool.key, next) : undefined}
                data-testid={`toggle-${tool.key}-${ch.key}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleSave = async () => {
    try {
      const payload = { ...form };
      try { payload.input_schema = JSON.parse(form.input_schema || '{}'); } catch { payload.input_schema = {}; }
      try { payload.output_schema = JSON.parse(form.output_schema || '{}'); } catch { payload.output_schema = {}; }
      if (editing) { await api.put(`/tenant/ai-tools/${editing}`, payload); toast.success('Herramienta actualizada'); }
      else { await api.post('/tenant/ai-tools', payload); toast.success('Herramienta creada'); }
      setTab('list'); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handlePublish = async (id) => {
    try { const { data } = await api.post(`/tenant/ai-tools/${id}/publish`); toast.success(`Publicada v${data.version}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleArchive = async (id) => {
    try { await api.patch(`/tenant/ai-tools/${id}/archive`); toast.success('Archivada'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleClone = async (key) => {
    try { await api.post(`/tenant/ai-tools/templates/${key}/clone`); toast.success('Template clonado'); load(); setTab('list'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleValidate = async (id) => {
    try {
      const { data } = await api.post(`/tenant/ai-tools/${id}/validate`, { sample_input: {} });
      setValidateResult(data);
      setLogsLoaded(false); // la prueba queda registrada: refrescar el historial al abrirlo
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, category: t.category, tool_type: t.tool_type, execution_mode: t.execution_mode, risk_level: t.risk_level, approval_mode: t.approval_mode, description: t.description || '', input_schema: JSON.stringify(t.input_schema || {}, null, 2), output_schema: JSON.stringify(t.output_schema || {}, null, 2) });
    setTab('edit');
  };
  const openCreate = () => { setEditing(null); setForm({ key:'', name:'', category:'custom', tool_type:'custom_future', execution_mode:'disabled', risk_level:'low', approval_mode:'none', description:'', input_schema:'{}', output_schema:'{}' }); setTab('edit'); };

  const tabs = [
    { k:'list', l:'Herramientas', icon: Wrench },
    { k:'templates', l:'Templates', icon: Copy },
    { k:'logs', l:'Historial de pruebas', icon: History },
  ];

  const [mcpEnabled, setMcpEnabled] = useState(false);
  useEffect(() => {
    api.get('/tenant/ai/limits').then(r => { if (r.data?.mcp_enabled) setMcpEnabled(true); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="ai-tool-registry">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><Wrench className="w-5 h-5" /> Herramientas</h2>
          <p className="text-sm text-[#64748B] mt-0.5">Enciende acciones reales que tu IA puede hacer, sin escribir código.</p>
        </div>
        <button
          onClick={() => setAdvancedMode(v => !v)}
          className="flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0F172A] transition-colors"
          data-testid="tools-advanced-toggle"
        >
          <Code2 className="w-3.5 h-3.5" /> {advancedMode ? 'Ocultar modo avanzado' : 'Modo avanzado (técnico)'}
        </button>
      </div>

      <SectionHelp
        module="tools"
        title={helpContent.tools.title}
        description={helpContent.tools.description}
        whatItDoesNot={helpContent.tools.whatItDoesNot}
      />

      {builtinLoading ? (
        <div className="flex justify-center py-10" data-testid="builtin-tools-loading">
          <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4" data-testid="builtin-tools-list">
          {shippedBuiltin.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Disponibles ahora</p>
              {shippedBuiltin.map((tool) => renderBuiltinToolCard(tool, { interactive: true }))}
            </div>
          )}
          {plannedBuiltin.length > 0 && (
            <details className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="builtin-tools-coming">
              <summary className="cursor-pointer text-xs font-semibold text-[#64748B] list-none flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-[#94A3B8]" />
                Lo que viene ({plannedBuiltin.length}) — aún no se pueden usar
              </summary>
              <p className="text-xs text-[#94A3B8] mt-2 mb-3">
                Están en el mapa de producto. No las actives: no hacen nada todavía.
              </p>
              <div className="space-y-3">
                {plannedBuiltin.map((tool) => renderBuiltinToolCard(tool, { interactive: false }))}
              </div>
            </details>
          )}
          {shippedBuiltin.length === 0 && plannedBuiltin.length === 0 && (
            <p className="text-sm text-[#64748B] text-center py-6">No hay herramientas en el catálogo. Recarga la página.</p>
          )}
        </div>
      )}

      {!advancedMode && (
        <p className="text-xs text-[#94A3B8]">
          ¿Necesitas una herramienta personalizada por código? Usa el "Modo avanzado" de arriba.
        </p>
      )}

      {advancedMode && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#E2E8F0]">
        <div>
          <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><Wrench className="w-4 h-4" /> Registro técnico de herramientas</h3>
          <p className="text-xs text-[#64748B] mt-0.5">{tools.length} herramienta{tools.length !== 1 ? 's' : ''} registrada{tools.length !== 1 ? 's' : ''} — requiere escribir el schema en JSON.</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-tool-btn"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>

      <VerticalExampleBox module="tools" />

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
        <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Sprint 11 registra herramientas. Ejecución gobernada disponible en Orchestrator (Config IA &rarr; Orchestrator).
      </div>

      {mcpEnabled && (
        <button onClick={() => navigate('/tenant/ai/tools/mcp')}
          className="flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0F172A] transition-colors" data-testid="mcp-link">
          <Server className="w-3.5 h-3.5" /> Conectores MCP
        </button>
      )}

      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tool-tab-${t.k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.k || (tab === 'edit' && t.k === 'list') ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* LIST */}
      {tab === 'list' && (
        <div className="space-y-2" data-testid="tool-list">
          {loading ? (
            <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>
          ) : tools.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <Wrench className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">No hay herramientas registradas. Clona un template o crea una personalizada.</p>
              <div className="flex gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={() => setTab('templates')} data-testid="empty-templates-cta"><Copy className="w-4 h-4 mr-1" /> Templates</Button>
                <Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Crear</Button>
              </div>
            </div>
          ) : tools.map(t => (
            <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 hover:border-[#94A3B8] transition-colors" data-testid={`tool-${t.id}`}>
              <div className="w-9 h-9 rounded-lg bg-[#F8F9FA] flex items-center justify-center flex-shrink-0"><Wrench className="w-4 h-4 text-[#64748B]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{t.name}</p>
                <p className="text-xs text-[#94A3B8]">{t.key} · {t.category} · {t.tool_type}</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#F1F5F9] text-[#475569]" title="Número de versión publicada">v{t.current_version ?? 0}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status] || 'bg-gray-100'}`}>{t.status}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${AVAIL_COLORS[t.availability] || 'bg-gray-100'}`}>{t.availability}</span>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Editar"><Settings2 className="w-3.5 h-3.5 text-[#64748B]" /></button>
                <button onClick={() => handleValidate(t.id)} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Validar"><Play className="w-3.5 h-3.5 text-[#64748B]" /></button>
                <button onClick={() => openVersions(t)} className="p-1.5 hover:bg-[#F8F9FA] rounded" title="Ver versiones publicadas de esta herramienta" data-testid={`tool-versions-${t.id}`}><Layers className="w-3.5 h-3.5 text-[#64748B]" /></button>
                {t.status === 'draft' && <button onClick={() => handlePublish(t.id)} className="p-1.5 hover:bg-emerald-50 rounded" title="Publicar"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /></button>}
                {t.status !== 'archived' && <button onClick={() => handleArchive(t.id)} className="p-1.5 hover:bg-red-50 rounded" title="Archivar"><Archive className="w-3.5 h-3.5 text-red-400" /></button>}
              </div>
            </div>
          ))}
          {validateResult && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setValidateResult(null)}>
              <div className="bg-white rounded-xl border shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="validate-modal">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="text-sm font-bold">Resultado de Validación (Dry Run)</h3>
                  <button onClick={() => setValidateResult(null)}><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4 space-y-3 text-xs">
                  <div className={`px-2 py-1 rounded ${validateResult.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {validateResult.valid ? 'Schema válido' : 'Schema con errores'}
                  </div>
                  <div className="bg-[#F8F9FA] rounded-lg p-2 text-[10px] text-[#64748B]">{validateResult.execution_note}</div>
                  {validateResult.dry_run_plan?.steps?.map((s, i) => (
                    <p key={i} className="text-[#334155]">{s}</p>
                  ))}
                  {validateResult.errors?.length > 0 && (
                    <div className="space-y-1">
                      {validateResult.errors.map((e, i) => <p key={i} className="text-red-600">{e}</p>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* VERSIONES PUBLICADAS */}
          {versionsTool && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={closeVersions}>
              <div className="bg-white rounded-xl border shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="versions-modal">
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold truncate">Versiones de "{versionsTool.name}"</h3>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">Versión en uso: v{versionsTool.current_version ?? 0}</p>
                  </div>
                  <button onClick={closeVersions} data-testid="versions-modal-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="bg-[#F8F9FA] rounded-lg px-3 py-2 text-[11px] text-[#64748B] flex items-start gap-2">
                    <Layers className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#94A3B8]" />
                    <span>Cada publicación crea una versión. Sirve como historial: todavía no se puede regresar a una versión anterior.</span>
                    <HelpTooltip text="Si editas una herramienta que ya estaba publicada, los cambios no quedan como versión nueva hasta que la vuelvas a publicar." />
                  </div>
                  {versionsLoading ? (
                    <div className="flex justify-center py-8" data-testid="versions-loading">
                      <div className="w-7 h-7 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
                    </div>
                  ) : versionsError ? (
                    <div className="space-y-2" data-testid="versions-error">
                      <p className="text-xs text-red-600">{versionsError}</p>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => openVersions(versionsTool)} data-testid="versions-retry">Reintentar</Button>
                    </div>
                  ) : versions.length === 0 ? (
                    <p className="text-xs text-[#64748B] text-center py-6" data-testid="versions-empty">
                      Sin versiones todavía. Publica la herramienta para guardar la primera.
                    </p>
                  ) : (
                    <div className="space-y-1.5" data-testid="versions-list">
                      {versions.map(v => (
                        <div key={v.id} className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid={`version-${v.id}`}>
                          <span className="text-xs font-semibold text-[#0F172A] w-10">v{v.version_number}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${VERSION_STATUS_STYLES[v.status] || 'bg-gray-100 text-gray-600'}`}>
                            {VERSION_STATUS_LABELS[v.status] || v.status}
                          </span>
                          <span className="text-[11px] text-[#94A3B8] ml-auto">{formatDateTime(v.published_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HISTORIAL DE PRUEBAS */}
      {tab === 'logs' && (
        <div className="space-y-2" data-testid="tool-logs">
          <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-3 py-2 text-xs text-[#64748B] flex items-start gap-2">
            <Play className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#94A3B8]" />
            <span>
              Aquí ves cada vez que probaste una herramienta en modo simulación; no se ejecutó ninguna acción real.
              Si una prueba salió con errores, vuelve a probarla con el botón <strong>Validar</strong> en la lista de herramientas para ver el detalle.
            </span>
            <HelpTooltip text="Una prueba en simulación revisa que la herramienta esté bien armada. Nunca crea, cambia ni envía nada a tus clientes." />
          </div>

          {logsLoading && logs.length === 0 ? (
            <div className="flex justify-center py-10" data-testid="tool-logs-loading">
              <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
            </div>
          ) : logsError && logs.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center" data-testid="tool-logs-error">
              <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">{logsError}</p>
              <Button size="sm" variant="outline" onClick={() => loadLogs(1, false)} data-testid="tool-logs-retry">Reintentar</Button>
            </div>
          ) : logs.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center" data-testid="tool-logs-empty">
              <History className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">Sin pruebas todavía. Usa el botón «Validar» en la lista de herramientas para hacer la primera.</p>
              <Button size="sm" variant="outline" onClick={() => setTab('list')} data-testid="tool-logs-go-list">
                <Wrench className="w-4 h-4 mr-1" /> Ir a herramientas
              </Button>
            </div>
          ) : (
            <>
              {logs.map(l => (
                <div key={l.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3" data-testid={`tool-log-${l.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                    <History className="w-4 h-4 text-[#64748B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{toolNameById(l.tool_id)}</p>
                    <p className="text-xs text-[#94A3B8]">{LOG_TYPE_LABELS[l.validation_type] || l.validation_type || 'Simulación'} · {formatDateTime(l.created_at)}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${LOG_STATUS_STYLES[l.status] || 'bg-gray-100 text-gray-600'}`}>
                    {LOG_STATUS_LABELS[l.status] || l.status}
                  </span>
                </div>
              ))}
              {logsError && (
                <p className="text-xs text-red-600 text-center py-1" data-testid="tool-logs-more-error">{logsError}</p>
              )}
              {logsHasMore && (
                <div className="flex justify-center pt-1">
                  <Button size="sm" variant="outline" className="text-xs" disabled={logsLoading}
                    onClick={() => loadLogs(logsPage + 1, true)} data-testid="tool-logs-load-more">
                    {logsLoading ? 'Cargando…' : 'Cargar más'}
                  </Button>
                </div>
              )}
              {!logsHasMore && (
                <p className="text-[11px] text-[#94A3B8] text-center pt-1">No hay más pruebas registradas.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* EDIT/CREATE */}
      {tab === 'edit' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4 max-w-2xl" data-testid="tool-form">
          <h3 className="text-sm font-bold">{editing ? 'Editar Herramienta' : 'Nueva Herramienta'}</h3>
          {!editing && <Input placeholder="Key (ej: crm.qualify_lead)" value={form.key} onChange={e => setForm(f => ({...f, key: e.target.value}))} data-testid="tool-key" />}
          <Input placeholder="Nombre" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="tool-name" />
          <Input placeholder="Descripción" value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} data-testid="tool-description" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Categoría</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="tool-category">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Tipo</label>
              <select value={form.tool_type} onChange={e => setForm(f => ({...f, tool_type: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="tool-type">{TOOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Ejecución</label>
              <select value={form.execution_mode} onChange={e => setForm(f => ({...f, execution_mode: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="tool-exec">{EXEC_MODES.map(m => <option key={m} value={m}>{m}</option>)}</select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Riesgo</label>
              <select value={form.risk_level} onChange={e => setForm(f => ({...f, risk_level: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="tool-risk">{RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}</select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Aprobación</label>
              <select value={form.approval_mode} onChange={e => setForm(f => ({...f, approval_mode: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="tool-approval">{APPROVAL_MODES.map(a => <option key={a} value={a}>{a}</option>)}</select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Input Schema (JSON)</label>
            <Textarea value={form.input_schema} onChange={e => setForm(f => ({...f, input_schema: e.target.value}))} rows={4} className="font-mono text-xs" data-testid="tool-input-schema" />
          </div>
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Output Schema (JSON)</label>
            <Textarea value={form.output_schema} onChange={e => setForm(f => ({...f, output_schema: e.target.value}))} rows={4} className="font-mono text-xs" data-testid="tool-output-schema" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setTab('list'); setEditing(null); }}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} data-testid="tool-save">{editing ? 'Actualizar' : 'Crear'}</Button>
          </div>
        </div>
      )}

      {/* TEMPLATES */}
      {tab === 'templates' && (
        <div className="space-y-2" data-testid="tool-templates">
          {templates.map(t => (
            <div key={t.key} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3" data-testid={`tmpl-${t.key}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><Copy className="w-4 h-4 text-blue-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{t.name}</p>
                <p className="text-xs text-[#94A3B8]">{t.key} · {t.category} · <span className={`${AVAIL_COLORS[t.availability] || ''} px-1 rounded`}>{t.availability}</span></p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleClone(t.key)} className="text-xs" data-testid={`clone-${t.key}`}><Copy className="w-3 h-3 mr-1" /> Clonar</Button>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
